import { fromLonLat, toLonLat } from "ol/proj";
import { parseLayerParams, serializeViews } from "./layerViews.mjs";
import { slugFromName } from "../config/layerSlug.mjs";

// ── Cached initial URL params (captured once at module load, before any
//    pushState call can overwrite the address bar) ─────────────────────────────

const _initialParams = new URLSearchParams(window.location.search);

// layers= / views= / revisions= are resolved together: a retired flavour slug in
// any of them decides a row's view, which decides which revisions survive.
const _initialLayerParams = parseLayerParams({
  layers: _initialParams.get("layers"),
  views: _initialParams.get("views"),
  revisions: _initialParams.get("revisions"),
});

// ── Initialization gate ───────────────────────────────────────────────────────
// pushState is a no-op until startSync() is called, preventing premature URL
// overwrites during the async auth + layer registration phase.

let _syncing = false;

export function startSync() {
  _syncing = true;

  // Wire map view changes
  if (_map) {
    _map.on("moveend", debouncedPushState);
  }

  // Wire layer toggle changes
  _layerControls.forEach((ctrl) => {
    ctrl.on("propertychange", function (e) {
      if (e.key === "vectorToggle" || e.key === "view") debouncedPushState();
    });
  });

  // Wire revision changes
  _revisionControls.forEach((rc) => {
    rc.on("propertychange", function (e) {
      if (e.key === "selectedRevisions") debouncedPushState();
    });
  });

  // Wire WKT changes
  if (_wktControl) {
    _wktControl.on("propertychange", function (e) {
      if (e.key === "wkt" || e.key === "wktToggle") debouncedPushState();
    });
  }

  // Wire date changes
  if (_dateControl) {
    _dateControl.on("propertychange", function (e) {
      if (e.key === "startDate" || e.key === "endDate") debouncedPushState();
    });
  }

  // Wire heatmap control changes
  if (_heatmapControl) {
    _heatmapControl.on("propertychange", function (e) {
      if (
        e.key === "heatmapToggle" ||
        e.key === "displayMode" ||
        e.key === "activeDirections"
      )
        debouncedPushState();
    });
  }

  // Wire MoMa traces toggle
  if (_momaControl) {
    _momaControl.on("propertychange", function (e) {
      if (e.key === "momaToggle") debouncedPushState();
    });
  }

  // Wire data source control changes
  if (_dataSourceControl) {
    _dataSourceControl.on("propertychange", function (e) {
      if (e.key === "dataSource") debouncedPushState();
    });
  }

  // Wire basemap (base layer) selection changes
  if (_basemapLayers) {
    _basemapLayers.forEach(({ layer }) => {
      layer.on("change:visible", debouncedPushState);
    });
  }

  // Defer the first URL write to the next event loop tick so that any
  // synchronous processing that follows the caller (e.g. idTokenClaims
  // role checks) has completed before we serialize.
  setTimeout(pushState, 0);

  // Expand any collapsible (<details>) that contains a checked layer
  _layerControls.forEach((ctrl) => {
    if (ctrl.get("vectorToggle") === true) {
      const details = ctrl.element.closest("details");
      if (details) details.open = true;
    }
  });
}

// ── Registry ──────────────────────────────────────────────────────────────────

let _map = null;
let _wktControl = null;
let _dateControl = null;
let _heatmapControl = null;
let _momaControl = null;
let _dataSourceControl = null;
let _productState = null;
let _basemapLayers = null; // [{ name, layer }] — base layers for basemap= param
const _layerControls = new Map(); // slug → VectorFeatureControl
const _revisionControls = new Map(); // slug → RevisionControl

export function registerMap(map) {
  _map = map;
}

export function registerBasemapLayers(layers) {
  _basemapLayers = layers;
}

export function registerLayerControl(slug, control) {
  _layerControls.set(slug, control);
}

export function registerRevisionControl(slug, revisionControl) {
  _revisionControls.set(slug, revisionControl);
}

export function getRevisionControls() {
  // returns Array<{ slug, control }> for fan-out
  return Array.from(_revisionControls.entries()).map(([slug, control]) => ({
    slug,
    control,
  }));
}

export function registerWKTControl(control) {
  _wktControl = control;
}

export function registerDateControl(control) {
  _dateControl = control;
}

export function registerHeatmapControl(control) {
  _heatmapControl = control;
}

export function registerMomaControl(control) {
  _momaControl = control;
}

export function registerDataSourceControl(control) {
  _dataSourceControl = control;
}

// ── Slug helpers ──────────────────────────────────────────────────────────────

// Base layers still derive their slug from their display name: there are three
// of them, the names are fixed, and `basemap=` has never been through a rename.
// Vector layers declare theirs instead — see config/layerSlug.mjs.
export const getLayerSlug = slugFromName;

// Given the base-layer names and the slug from the URL, return the name to show
// exclusively, or null to fall back to the default (all-visible) behaviour.
export function resolveInitialBasemap(names, urlSlug) {
  if (!urlSlug) return null;
  return names.find((name) => getLayerSlug(name) === urlSlug) || null;
}

// Slug of the currently-visible base layer (for serialization), or null.
function visibleBasemapSlug(basemapLayers) {
  const entry = basemapLayers.find(({ layer }) => layer.getVisible());
  return entry ? getLayerSlug(entry.name) : null;
}

// ── Serialize ─────────────────────────────────────────────────────────────────

function serializeState() {
  const params = new URLSearchParams();

  if (_map) {
    const view = _map.getView();
    const center = view.getCenter();
    if (center) {
      const [lon, lat] = toLonLat(center);
      params.set("center", `${lon.toFixed(6)},${lat.toFixed(6)}`);
    }
    const zoom = view.getZoom();
    if (zoom != null) {
      params.set("zoom", zoom.toFixed(4));
    }
  }

  if (_basemapLayers) {
    const basemap = visibleBasemapSlug(_basemapLayers);
    if (basemap) params.set("basemap", basemap);
  }

  const activeSlugs = [];
  _layerControls.forEach((ctrl, slug) => {
    if (ctrl.get("vectorToggle") === true) activeSlugs.push(slug);
  });
  if (activeSlugs.length > 0) {
    params.set("layers", activeSlugs.join(","));
  }

  // Written for every visible row that has views, default included, so a later
  // change of default can't flip the view an existing link opens in.
  const activeViews = [];
  _layerControls.forEach((ctrl, slug) => {
    if (ctrl.get("vectorToggle") === true && ctrl.get("view")) {
      activeViews.push([slug, ctrl.get("view")]);
    }
  });
  if (activeViews.length > 0) {
    params.set("views", serializeViews(activeViews));
  }

  // Only include revisions for layers that are currently visible, scoped by slug
  const allRevisions = [];
  _revisionControls.forEach((rc, slug) => {
    const layerCtrl = _layerControls.get(slug);
    if (layerCtrl && layerCtrl.get("vectorToggle") === true) {
      const revisions = rc.get("selectedRevisions");
      if (revisions) {
        revisions.forEach(({ layerId, revision }) => {
          allRevisions.push(`${slug}~${layerId}:${revision}`);
        });
      }
    }
  });
  if (allRevisions.length > 0) {
    params.set("revisions", allRevisions.join(","));
  }

  if (_productState && _productState.scope) {
    params.set("product", _productState.scope);
    if (_productState.version) params.set("version", _productState.version);
  }

  if (_wktControl) {
    const wkt = _wktControl.get("wkt");
    if (wkt) params.set("wkt", wkt);
    if (_wktControl.get("wktToggle") === false) params.set("wktToggle", "0");
  }

  if (_dateControl) {
    const startDate = _dateControl.get("startDate");
    const endDate = _dateControl.get("endDate");
    if (startDate) params.set("startDate", startDate);
    if (endDate) params.set("endDate", endDate);
  }

  if (_heatmapControl) {
    if (_heatmapControl.get("heatmapToggle") === true) params.set("fcd", "1");
    const mode = _heatmapControl.get("displayMode");
    if (mode && mode !== "density") params.set("fcdMode", mode);
    const dirs = _heatmapControl.get("activeDirections");
    if (dirs != null && dirs !== "1,2,3,4,5,6,7,8,9,10,11,12")
      params.set("fcdDirs", dirs);
  }

  if (_dataSourceControl) {
    const ds = _dataSourceControl.get("dataSource");
    if (ds != null && ds !== 4) params.set("fcdSrc", ds);
  }

  if (_momaControl && _momaControl.get("momaToggle") === true) {
    params.set("moma", "1");
  }

  return params;
}

// ── Push to address bar ───────────────────────────────────────────────────────

export function pushState() {
  if (!_syncing) return;
  const params = serializeState();
  const newUrl = `${window.location.pathname}?${params.toString()}`;
  window.history.replaceState(null, "", newUrl);
}

let _debounceTimer = null;

export function debouncedPushState() {
  if (!_syncing) return;
  clearTimeout(_debounceTimer);
  _debounceTimer = setTimeout(pushState, 300);
}

// ── Deserialize (always reads from the cached initial URL) ────────────────────

export function getLayersFromURL() {
  // Legacy and flavour slugs are translated on the way in (layerViews.mjs), so
  // everything downstream only ever sees current ones, deduped.
  return _initialLayerParams.layers;
}

// Map of row slug → view id, for rows the URL names a view for.
export function getViewsFromURL() {
  return _initialLayerParams.views;
}

export function getBasemapFromURL() {
  return _initialParams.get("basemap");
}

// Pure parser — returns `{ center, zoom }` or null. Accepts a
// URLSearchParams or anything URLSearchParams-compatible (i.e. `.get()`).
// Extracted so entries that don't use the full URLStateManager registry
// (e.g. caj-editor.js) can share the center/zoom parsing.
export function parseViewFromParams(params) {
  const centerStr = params.get("center");
  const zoomStr = params.get("zoom");
  if (!centerStr || !zoomStr) return null;
  const [lon, lat] = centerStr.split(",").map(parseFloat);
  if (isNaN(lon) || isNaN(lat)) return null;
  const zoom = parseFloat(zoomStr);
  if (isNaN(zoom)) return null;
  return { center: fromLonLat([lon, lat]), zoom };
}

export function getViewFromURL() {
  return parseViewFromParams(_initialParams);
}

export function getRevisionsFromURL() {
  // Map of slug → [{layerId, revision}] so each OL layer only restores its own
  // revisions even if WFS layer IDs overlap across endpoints.
  return _initialLayerParams.revisions;
}

export function setProductState(scope, version) {
  // version may be null when a product is selected but no report is applied.
  _productState = scope ? { scope, version: version || null } : null;
  debouncedPushState();
}
export function getInitialProductState() {
  const scope = _initialParams.get("product");
  const version = _initialParams.get("version");
  return scope ? { scope, version: version || null } : null;
}

export function getWKTToggleFromURL() {
  const raw = _initialParams.get("wktToggle");
  return raw !== "0"; // default true
}

export function getMomaToggleFromURL() {
  return _initialParams.get("moma") === "1";
}

export function getFCDToggleFromURL() {
  return _initialParams.get("fcd") === "1";
}

export function getFCDModeFromURL() {
  return _initialParams.get("fcdMode") || "density";
}

export function getFCDDirectionsFromURL() {
  const raw = _initialParams.get("fcdDirs");
  if (!raw) return null;
  return raw.split(",").filter(Boolean);
}

export function getFCDDataSourceFromURL() {
  const raw = _initialParams.get("fcdSrc");
  if (raw == null) return null;
  const n = Number(raw);
  return isNaN(n) ? null : n;
}
